import { createContext, useContext, useReducer, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type {
  HybridRegistrationState,
  HybridStage,
  HybridTrack,
  HybridWriter,
  HybridSplit,
  HybridComponent,
  StateMutation,
} from '../types/hybrid';

// --- Persistence state ---

interface HybridPersistenceState {
  registrationId: string | null;
  saveStatus: 'idle' | 'saving' | 'saved' | 'error';
  lastSaved: Date | null;
}

type FullState = HybridRegistrationState & HybridPersistenceState;

// --- Initial state ---

const initialState: FullState = {
  stage: 1,
  track: { title: '', artists: [], source: '', confirmed: false },
  writers: [],
  splits: [],
  sessionId: null,
  registrationId: null,
  saveStatus: 'idle',
  lastSaved: null,
};

// --- Reducer ---

type Action =
  | { type: 'SET_STAGE'; stage: HybridStage }
  | { type: 'UPDATE_TRACK'; updates: Partial<HybridTrack> }
  | { type: 'SET_WRITERS'; writers: HybridWriter[] }
  | { type: 'UPDATE_WRITER'; writerId: string; updates: Partial<HybridWriter> }
  | { type: 'SET_SPLITS'; splits: HybridSplit[] }
  | { type: 'SET_SESSION_ID'; sessionId: string }
  | { type: 'APPLY_COMPONENTS'; components: HybridComponent[] }
  | { type: 'UPDATE_SPLIT'; writerId: string; updates: Partial<HybridSplit> }
  | { type: 'SET_REGISTRATION_ID'; registrationId: string }
  | { type: 'SET_SAVE_STATUS'; status: 'idle' | 'saving' | 'saved' | 'error' }
  | { type: 'SET_LAST_SAVED'; date: Date }
  | { type: 'LOAD'; payload: Partial<FullState> }
  | { type: 'RESET' };

function reducer(state: FullState, action: Action): FullState {
  switch (action.type) {
    case 'SET_STAGE':
      return { ...state, stage: action.stage };

    case 'UPDATE_TRACK':
      return { ...state, track: { ...state.track, ...action.updates } };

    case 'SET_WRITERS':
      return { ...state, writers: action.writers };

    case 'UPDATE_WRITER':
      return {
        ...state,
        writers: state.writers.map((w) =>
          w.writerId === action.writerId ? { ...w, ...action.updates } : w,
        ),
      };

    case 'SET_SPLITS':
      return { ...state, splits: action.splits };

    case 'UPDATE_SPLIT':
      return {
        ...state,
        splits: state.splits.map((s) =>
          s.writerId === action.writerId ? { ...s, ...action.updates } : s,
        ),
      };

    case 'SET_SESSION_ID':
      return { ...state, sessionId: action.sessionId };

    case 'APPLY_COMPONENTS': {
      let next = { ...state };
      for (const comp of action.components) {
        switch (comp.type) {
          case 'track_card':
            next = {
              ...next,
              track: {
                title: comp.title,
                artists: comp.artists,
                source: comp.source,
                confirmed: next.track.confirmed,
              },
            };
            break;
          case 'writer_card': {
            const existing = next.writers.findIndex((w) => w.writerId === comp.writerId);
            const baseWriter = {
              writerId: comp.writerId,
              name: comp.name,
              selectedIPI: comp.selectedIPI,
              confidence: comp.confidence,
              allMatches: comp.allMatches,
              knownWorks: comp.knownWorks,
              publisher: comp.publisher,
              contractType: comp.contractType,
              stageNameCheck: comp.stageNameCheck,
              confirmed: comp.confirmed,
              knownAlias: comp.knownAlias ?? '',
              pending: comp.pending ?? false,
              pendingStage: comp.pendingStage ?? null,
            };
            if (existing >= 0) {
              // Preserve the first-seen snapshot so Reset keeps working.
              const prev = next.writers[existing];
              // Defensive: once the user has locally confirmed a writer, a
              // stale server response (from a race between concurrent
              // mutations) must not be allowed to un-confirm it. Local
              // confirmation only clears via an explicit unconfirm action.
              const confirmed = prev.confirmed || baseWriter.confirmed;
              const writer: HybridWriter = {
                ...baseWriter,
                confirmed,
                serverOriginal: prev.serverOriginal ?? { ...baseWriter },
              };
              const writers = [...next.writers];
              writers[existing] = writer;
              next = { ...next, writers };
            } else {
              const writer: HybridWriter = {
                ...baseWriter,
                serverOriginal: { ...baseWriter },
              };
              next = { ...next, writers: [...next.writers, writer] };
            }
            break;
          }
          case 'splits_card':
            next = { ...next, splits: comp.splits };
            break;
          case 'summary_card':
            // Summary payload carries ISRC + duration that earlier cards don't
            // — hoist them onto the track so the Registration Summary can show
            // them without a separate prop.
            next = {
              ...next,
              track: {
                ...next.track,
                title: comp.track.title || next.track.title,
                artists: comp.track.artists?.length ? comp.track.artists : next.track.artists,
                isrc: comp.track.isrc ?? next.track.isrc ?? null,
                duration: comp.track.duration ?? next.track.duration ?? null,
              },
            };
            break;
          case 'comparison_card':
          case 'unreleased_rescue_card':
            // No persistent reducer state — these cards render from message components directly.
            break;
        }
      }
      return next;
    }

    case 'SET_REGISTRATION_ID':
      return { ...state, registrationId: action.registrationId };

    case 'SET_SAVE_STATUS':
      return { ...state, saveStatus: action.status };

    case 'SET_LAST_SAVED':
      return { ...state, lastSaved: action.date };

    case 'LOAD':
      return { ...state, ...action.payload };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}

// --- Context ---

interface HybridRegistrationContextValue {
  state: HybridRegistrationState & HybridPersistenceState;
  setStage: (stage: HybridStage) => void;
  updateTrack: (updates: Partial<HybridTrack>) => void;
  updateWriter: (writerId: string, updates: Partial<HybridWriter>) => void;
  updateSplits: (splits: HybridSplit[]) => void;
  applyComponents: (components: HybridComponent[]) => void;
  applyMutations: (mutations: StateMutation[]) => void;
  reset: () => void;
}

const HybridRegistrationContext = createContext<HybridRegistrationContextValue | null>(null);

export function HybridRegistrationProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedSnapshotRef = useRef<string>('');
  const stateRef = useRef(state);
  stateRef.current = state;

  // --- Load draft from URL param on mount ---
  useEffect(() => {
    const resumeId = searchParams.get('resume');
    if (resumeId && isSupabaseConfigured() && user) {
      loadRegistration(resumeId);
    }
  }, [searchParams, user]);

  const loadRegistration = async (id: string) => {
    try {
      const { data, error } = await supabase
        .from('registrations')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!data) return;

      // The hybrid state is stored in the writers/splits JSON columns
      // with a registration_type marker
      dispatch({
        type: 'LOAD',
        payload: {
          registrationId: data.id,
          stage: (data.current_step as HybridStage) || 1,
          track: {
            title: data.title || '',
            artists: data.additional_artists || (data.artist ? [data.artist] : []),
            source: '',
            confirmed: (data.current_step || 1) >= 2,
          },
          writers: (data.writers as HybridWriter[]) || [],
          splits: (data.splits as HybridSplit[]) || [],
          lastSaved: new Date(data.updated_at),
          saveStatus: 'saved',
        },
      });

      // Clear the resume param from URL
      setSearchParams({});
    } catch (err) {
      console.error('Failed to load hybrid registration:', err);
    }
  };

  // --- Save to Supabase ---
  const saveToSupabase = useCallback(async () => {
    const s = stateRef.current;
    if (!isSupabaseConfigured() || !user) return;

    // Create snapshot for change detection
    const snapshot = JSON.stringify({
      stage: s.stage,
      track: s.track,
      writers: s.writers,
      splits: s.splits,
    });
    if (snapshot === lastSavedSnapshotRef.current) return;

    dispatch({ type: 'SET_SAVE_STATUS', status: 'saving' });

    try {
      const registrationData = {
        user_id: user.id,
        status: 'draft' as const,
        title: s.track.title || null,
        artist: s.track.artists.join(', ') || null,
        additional_artists: s.track.artists,
        writers: s.writers,
        splits: s.splits,
        current_step: s.stage,
        registration_type: 'hybrid',
      };

      let result;

      if (s.registrationId) {
        result = await supabase
          .from('registrations')
          .update(registrationData)
          .eq('id', s.registrationId)
          .select()
          .single();
      } else {
        result = await supabase
          .from('registrations')
          .insert(registrationData)
          .select()
          .single();
      }

      if (result.error) throw result.error;

      if (result.data && !s.registrationId) {
        dispatch({ type: 'SET_REGISTRATION_ID', registrationId: result.data.id });
      }

      lastSavedSnapshotRef.current = snapshot;
      dispatch({ type: 'SET_SAVE_STATUS', status: 'saved' });
      dispatch({ type: 'SET_LAST_SAVED', date: new Date() });
    } catch (err) {
      console.error('Failed to save hybrid registration:', err);
      dispatch({ type: 'SET_SAVE_STATUS', status: 'error' });
    }
  }, [user]);

  // Debounced auto-save on data changes (3 seconds)
  useEffect(() => {
    if (!isSupabaseConfigured() || !user) return;
    // Don't save empty state
    if (!state.track.title && state.writers.length === 0) return;

    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);

    saveTimeoutRef.current = setTimeout(() => {
      saveToSupabase();
    }, 3000);

    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [state.track, state.writers, state.splits, state.stage]);

  // --- Actions ---

  const setStage = useCallback((stage: HybridStage) => {
    dispatch({ type: 'SET_STAGE', stage });
  }, []);

  const updateTrack = useCallback((updates: Partial<HybridTrack>) => {
    dispatch({ type: 'UPDATE_TRACK', updates });
  }, []);

  const updateWriter = useCallback((writerId: string, updates: Partial<HybridWriter>) => {
    dispatch({ type: 'UPDATE_WRITER', writerId, updates });
  }, []);

  const updateSplits = useCallback((splits: HybridSplit[]) => {
    dispatch({ type: 'SET_SPLITS', splits });
  }, []);

  const applyComponents = useCallback((components: HybridComponent[]) => {
    dispatch({ type: 'APPLY_COMPONENTS', components });
  }, []);

  const applyMutations = useCallback((mutations: StateMutation[]) => {
    for (const m of mutations) {
      switch (m.action) {
        case 'updateTrack':
          dispatch({ type: 'UPDATE_TRACK', updates: { [m.field]: m.value } });
          break;
        case 'updateWriter':
          if (m.target) {
            dispatch({
              type: 'UPDATE_WRITER',
              writerId: m.target,
              updates: { [m.field]: m.value },
            });
          }
          break;
        case 'updateSplit':
          if (m.target) {
            dispatch({
              type: 'UPDATE_SPLIT',
              writerId: m.target,
              updates: { [m.field]: m.value },
            });
          }
          break;
      }
    }
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  return (
    <HybridRegistrationContext.Provider
      value={{
        state,
        setStage,
        updateTrack,
        updateWriter,
        updateSplits,
        applyComponents,
        applyMutations,
        reset,
      }}
    >
      {children}
    </HybridRegistrationContext.Provider>
  );
}

export function useHybridRegistration() {
  const ctx = useContext(HybridRegistrationContext);
  if (!ctx) {
    throw new Error('useHybridRegistration must be used within HybridRegistrationProvider');
  }
  return ctx;
}
